import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavHeader from '@/components/NavHeader';
import { useAuth } from '@pooflabs/web';
import { PageLayout } from '@/components/poof-ui';
import { Button } from '@/components/ui/button';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyLocks, subscribeAllLocks } from '@/lib/collections/locks';
import type { LocksResponse } from '@/lib/collections/locks';
import { subscribeManyDonations, subscribeAllDonations } from '@/lib/collections/donations';
import type { DonationsResponse } from '@/lib/collections/donations';
import { BlurFade } from '@/components/effects/BlurFade';
import { Particles } from '@/components/effects/Particles';
import { LockCard } from '@/components/LockCard';
import { CreateLockModal } from '@/components/CreateLockModal';
import { EmptyState } from '@/components/poof-ui';
import { getTokenByMint, formatTokenAmount, SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON } from '@/utils/tokenConfig';
import {
  buildLeaderboard,
  calculateLockPoints,
  calculateDonationPoints,
  formatPoints,
  sumPendingAwarePoints,
} from '@/utils/pointsCalculator';
import { warmUsdPrices } from '@/utils/usdPricing';
import { Lock, Plus, History, Shield, Wallet, Skull, Trophy, Heart, Clock } from 'lucide-react';
import { subscribeAllPrizePoolDeposits } from '@/lib/collections/prizePoolDeposits';
import type { PrizePoolDepositsResponse } from '@/lib/collections/prizePoolDeposits';
import { PrizePoolSummaryCard } from '@/components/PrizePoolsPage';

interface SummaryCardProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  delay: number;
  gold?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, sub, icon, delay, gold }) => (
  <BlurFade delay={delay}>
    <div
      className="rounded-xl p-6 relative overflow-hidden group"
      style={{
        background: gold
          ? 'linear-gradient(135deg, hsl(38 30% 8%) 0%, hsl(38 20% 10%) 100%)'
          : 'hsl(30 6% 7%)',
        border: gold ? '1px solid hsl(38 65% 57% / 0.35)' : '1px solid hsl(35 12% 14%)',
      }}
    >
      <div
        className="absolute top-0 right-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'radial-gradient(circle at top right, hsl(38 65% 57% / 0.08), transparent)',
        }}
      />
      <div className="flex items-start justify-between mb-3">
        <span
          className="text-xs font-medium tracking-wide uppercase"
          style={{ color: 'hsl(35 8% 52%)' }}
        >
          {label}
        </span>
        <span style={{ color: gold ? 'hsl(43 75% 68%)' : 'hsl(38 65% 57%)' }}>{icon}</span>
      </div>
      <div
        className="text-2xl font-semibold"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: gold ? 'hsl(43 75% 68%)' : 'hsl(43 75% 68%)',
        }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-xs mt-1" style={{ color: 'hsl(35 8% 45%)' }}>
          {sub}
        </div>
      )}
    </div>
  </BlurFade>
);

export const DashboardPage: React.FC = () => {
  const { user, login, loading: authLoading } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);

  // Points below read from usdPricing's module-level cache synchronously, which
  // isn't itself reactive — warm it (staggered, so we don't burst Jupiter) and
  // force a re-render as each price resolves so points update out of "pending".
  const [, bumpPriceTick] = useState(0);
  useEffect(() => {
    const cancel = warmUsdPrices([SOL, BONK, SKR, MEW, ANGELIC_SOL, GMT, DRAGON], () => {
      bumpPriceTick((n) => n + 1);
    });
    return cancel;
  }, []);

  const walletAddress = user?.address;

  const filterStr = walletAddress ? `where owner = '${walletAddress}'` : '';

  const { data: myLocks } = useRealtimeData<LocksResponse[]>(
    subscribeManyLocks,
    !!walletAddress,
    filterStr
  );

  // Fetch all locks to compute global leaderboard rank
  const { data: allLocks } = useRealtimeData<LocksResponse[]>(
    subscribeAllLocks,
    true
  );

  // Fetch my donations
  const donorFilter = walletAddress ? `where donor = '${walletAddress}'` : '';
  const { data: myDonations } = useRealtimeData<DonationsResponse[]>(
    subscribeManyDonations,
    !!walletAddress,
    donorFilter,
  );

  // Fetch all donations to compute accurate leaderboard rank (includes donation points)
  const { data: allDonations } = useRealtimeData<DonationsResponse[]>(
    subscribeAllDonations,
    true,
  );

  // Fetch prize pool deposits
  const { data: allPrizeDeposits } = useRealtimeData<PrizePoolDepositsResponse[]>(
    subscribeAllPrizePoolDeposits,
    true,
  );

  const locks = myLocks ?? [];
  const activeLocks = locks.filter((l) => l.status === 'active');
  const historyLocks = locks.filter((l) => l.status !== 'active');
  const permaLocks = activeLocks.filter((l) => l.lockType === 'perma');
  const standardLocks = activeLocks.filter((l) => l.lockType !== 'perma');

  // Multi-token totals by mint
  const tokenTotals: Record<string, number> = {};
  for (const l of activeLocks) {
    const mint = l.tokenMint ?? SOL;
    tokenTotals[mint] = (tokenTotals[mint] ?? 0) + (l.lkAmt ?? 0);
  }

  // Format multi-token summary string
  const tokenSummaryParts = [SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON]
    .filter((mint) => (tokenTotals[mint] ?? 0) > 0)
    .map((mint) => {
      const t = getTokenByMint(mint);
      const divisor = Math.pow(10, t.decimals);
      const amount = (tokenTotals[mint] ?? 0) / divisor;
      const fmt = amount < 1000
        ? amount.toLocaleString('en-US', { maximumFractionDigits: t.decimals <= 5 ? 0 : 2 })
        : Math.round(amount).toLocaleString('en-US');
      return `${fmt} ${t.symbol}`;
    });

  const totalLockedDisplay = tokenSummaryParts.length > 0
    ? tokenSummaryParts.join(' · ')
    : '0';


  // Points calculation. Locks/donations whose USD price is currently unavailable
  // return null from calculateLockPoints/calculateDonationPoints — they're excluded
  // from the totals below (never folded in as 0) and counted as "pending" instead.
  const { total: myLockPoints, pendingCount: pendingLockPoints } = sumPendingAwarePoints(
    locks.map((lock) => calculateLockPoints(lock)),
  );

  // Donation points: group by batchId and calculate per batch
  const { total: donationBatchPoints, pendingCount: pendingDonationPoints } = (() => {
    const donations = myDonations ?? [];
    const batchMap = new Map<string, { totalAmount: number; recipientCount: number; tokenMint: string }>();
    for (const d of donations) {
      const key = d.batchId;
      if (!batchMap.has(key)) batchMap.set(key, { totalAmount: 0, recipientCount: 0, tokenMint: d.tokenMint });
      const b = batchMap.get(key)!;
      b.totalAmount += d.amount;
      b.recipientCount += 1;
    }
    const batchPoints: Array<number | null> = [];
    for (const b of batchMap.values()) {
      const dec = getTokenByMint(b.tokenMint).decimals;
      const humanAmt = b.totalAmount / Math.pow(10, dec);
      batchPoints.push(calculateDonationPoints(humanAmt, b.recipientCount, 0, b.tokenMint));
    }
    return sumPendingAwarePoints(batchPoints);
  })();
  const myTotalPoints = myLockPoints + donationBatchPoints;
  const myPendingPoints = pendingLockPoints + pendingDonationPoints;

  // Donation summary stats
  const donationsSummary = (() => {
    const donations = myDonations ?? [];
    const uniqueRecipients = new Set(donations.map((d) => d.recipient));
    const tokenTotalsMap: Record<string, number> = {};
    for (const d of donations) {
      tokenTotalsMap[d.tokenMint] = (tokenTotalsMap[d.tokenMint] ?? 0) + d.amount;
    }
    return { totalRecipients: uniqueRecipients.size, tokenTotalsMap };
  })();

  const leaderboard = buildLeaderboard(allLocks ?? [], allDonations ?? []);
  const myRank = walletAddress
    ? leaderboard.findIndex((e) => e.address === walletAddress) + 1
    : 0;

  if (!authLoading && !user) {
    return (
      <PageLayout fullBleed footer={false}>
        <NavHeader />
        <div className="relative min-h-[80vh] flex items-center justify-center pt-16">
          <Particles quantity={20} color="hsl(38 65% 57%)" />
          <div className="relative z-10 text-center max-w-md mx-auto px-6">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{
                background: 'hsl(38 65% 57% / 0.12)',
                border: '1px solid hsl(38 65% 57% / 0.3)',
              }}
            >
              <Wallet size={24} style={{ color: 'hsl(38 65% 57%)' }} />
            </div>
            <h2
              className="text-3xl font-semibold mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Connect Your Wallet
            </h2>
            <p className="text-sm mb-8 leading-relaxed" style={{ color: 'hsl(35 8% 52%)' }}>
              To view your locks and create new ones, connect your Solana wallet.
            </p>
            <Button
              size="lg"
              onClick={() => login()}
              className="px-10 py-5 font-medium"
              style={{
                background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                color: 'hsl(30 8% 4%)',
                border: 'none',
              }}
            >
              Connect Wallet
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout fullBleed footer={false}>
      {/* Nav */}
      <NavHeader />

      <div className="container py-10">
        {/* Page header */}
        <BlurFade delay={0.05}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
            <div>
              <h1
                className="text-3xl font-semibold mb-1"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Your Vault
              </h1>
              <p className="text-sm" style={{ color: 'hsl(35 8% 52%)' }}>
                {walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}
              </p>
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="self-start sm:self-auto px-6 py-5 font-medium"
              style={{
                background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                color: 'hsl(30 8% 4%)',
                border: 'none',
                boxShadow: '0 0 24px hsl(38 65% 57% / 0.2)',
              }}
            >
              <Plus size={16} className="mr-2" />
              New Lock
            </Button>
          </div>
        </BlurFade>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <SummaryCard
            label="Total Locked"
            value={totalLockedDisplay}
            sub={`across ${activeLocks.length} active lock${activeLocks.length !== 1 ? 's' : ''}`}
            icon={<Lock size={16} />}
            delay={0.1}
          />
          <SummaryCard
            label="Active Locks"
            value={String(activeLocks.length)}
            sub={
              permaLocks.length > 0
                ? `${standardLocks.length} standard · ${permaLocks.length} perma`
                : `${historyLocks.length} completed`
            }
            icon={permaLocks.length > 0 ? <Skull size={16} /> : <Shield size={16} />}
            delay={0.15}
          />
          <SummaryCard
            label="All-Time Locks"
            value={String(locks.length)}
            sub={`${historyLocks.length} completed`}
            icon={<Wallet size={16} />}
            delay={0.2}
          />
          {/* Points card */}
          <BlurFade delay={0.25}>
            <Link to="/leaderboard" className="block group">
              <div
                className="rounded-xl p-6 relative overflow-hidden h-full transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, hsl(38 30% 8%) 0%, hsl(38 20% 10%) 100%)',
                  border: '1px solid hsl(38 65% 57% / 0.35)',
                  boxShadow: '0 0 0 hsl(38 65% 57% / 0.15)',
                }}
              >
                <div
                  className="absolute top-0 right-0 w-24 h-24 opacity-30 group-hover:opacity-60 transition-opacity duration-500"
                  style={{
                    background:
                      'radial-gradient(circle at top right, hsl(43 75% 68% / 0.3), transparent)',
                  }}
                />
                <div className="flex items-start justify-between mb-3">
                  <span
                    className="text-xs font-medium tracking-wide uppercase"
                    style={{ color: 'hsl(35 8% 52%)' }}
                  >
                    Selfless Points
                  </span>
                  <Trophy size={16} style={{ color: 'hsl(43 75% 68%)' }} />
                </div>
                <div
                  className="text-2xl font-semibold"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(43 75% 68%)' }}
                >
                  {formatPoints(myTotalPoints)}
                </div>
                {myRank > 0 ? (
                  <div className="text-xs mt-1" style={{ color: 'hsl(38 45% 45%)' }}>
                    Rank #{myRank} of {leaderboard.length} · View Leaderboard →
                  </div>
                ) : (
                  <div className="text-xs mt-1" style={{ color: 'hsl(35 8% 40%)' }}>
                    View Leaderboard →
                  </div>
                )}
                {myPendingPoints > 0 && (
                  <div className="text-xs mt-1 flex items-center gap-1" style={{ color: 'hsl(35 8% 45%)' }}>
                    <Clock size={10} />
                    {myPendingPoints} {myPendingPoints === 1 ? 'entry' : 'entries'} pending pricing
                  </div>
                )}
              </div>
            </Link>
          </BlurFade>
        </div>

        {/* Prize Pools summary card */}
        <BlurFade delay={0.26}>
          <Link to="/prize-pools" className="block group mb-4">
            <PrizePoolSummaryCard
              deposits={allPrizeDeposits ?? []}
              walletAddress={walletAddress}
            />
          </Link>
        </BlurFade>

        {/* Donations summary card */}
        <BlurFade delay={0.28}>
          <Link to="/donate" className="block group mb-10">
            <div
              className="rounded-xl p-5 relative overflow-hidden transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, hsl(168 25% 7%) 0%, hsl(168 18% 9%) 100%)',
                border: '1px solid hsl(168 70% 42% / 0.25)',
              }}
            >
              <div
                className="absolute top-0 right-0 w-40 h-20 opacity-20 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at top right, hsl(168 80% 48% / 0.4), transparent)' }}
              />
              <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'hsl(168 70% 42% / 0.15)', border: '1px solid hsl(168 70% 42% / 0.3)' }}
                  >
                    <Heart size={16} style={{ color: 'hsl(168 80% 48%)' }} />
                  </div>
                  <div>
                    <span className="text-xs font-medium tracking-wide uppercase" style={{ color: 'hsl(168 30% 45%)' }}>
                      Donations
                    </span>
                    <div
                      className="text-xl font-semibold"
                      style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(168 80% 72%)' }}
                    >
                      {donationsSummary.totalRecipients.toLocaleString()} people reached
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6 sm:ml-auto flex-wrap">
                  {[SOL, BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON]
                    .filter((mint) => (donationsSummary.tokenTotalsMap[mint] ?? 0) > 0)
                    .map((mint) => {
                      const t = getTokenByMint(mint);
                      return (
                        <div key={mint} className="text-center">
                          <div className="text-xs uppercase tracking-wide mb-0.5" style={{ color: 'hsl(168 30% 40%)' }}>{t.symbol}</div>
                          <div className="text-sm font-bold" style={{ color: 'hsl(168 80% 60%)' }}>
                            {formatTokenAmount(donationsSummary.tokenTotalsMap[mint] ?? 0, mint)}
                          </div>
                        </div>
                      );
                    })}
                  {donationsSummary.totalRecipients === 0 && (
                    <span className="text-sm" style={{ color: 'hsl(168 30% 40%)' }}>No donations yet — spread some wealth →</span>
                  )}
                  {donationsSummary.totalRecipients > 0 && (
                    <div className="text-center">
                      <div className="text-xs uppercase tracking-wide mb-0.5" style={{ color: 'hsl(168 30% 40%)' }}>Donation Pts</div>
                      <div className="text-sm font-bold" style={{ color: 'hsl(43 75% 68%)' }}>+{formatPoints(donationBatchPoints)}</div>
                    </div>
                  )}
                  <span className="text-xs" style={{ color: 'hsl(168 30% 40%)' }}>Donate more →</span>
                </div>
              </div>
            </div>
          </Link>
        </BlurFade>

        {/* Active locks */}
        <BlurFade delay={0.3}>
          <div className="mb-2">
            <div className="flex items-center gap-2 mb-4">
              <Lock size={16} style={{ color: 'hsl(38 65% 57%)' }} />
              <h2
                className="text-xl font-semibold"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Active Locks
              </h2>
              {activeLocks.length > 0 && (
                <span
                  className="ml-1 text-xs px-2 py-0.5 rounded-full"
                  style={{
                    background: 'hsl(38 65% 57% / 0.15)',
                    color: 'hsl(43 75% 68%)',
                    border: '1px solid hsl(38 65% 57% / 0.25)',
                  }}
                >
                  {activeLocks.length}
                </span>
              )}
            </div>

            {activeLocks.length === 0 ? (
              <EmptyState
                icon={<Lock />}
                title="No active locks"
                description="Create your first time-locked vault to begin building discipline."
                action={
                  <Button
                    onClick={() => setCreateOpen(true)}
                    style={{
                      background: 'linear-gradient(135deg, hsl(38 65% 52%), hsl(43 75% 60%))',
                      color: 'hsl(30 8% 4%)',
                      border: 'none',
                    }}
                  >
                    <Plus size={14} className="mr-2" />
                    Create First Lock
                  </Button>
                }
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeLocks.map((lock, i) => (
                  <BlurFade key={lock.id} delay={0.1 + i * 0.05}>
                    <LockCard lockId={lock.id} lock={lock} />
                  </BlurFade>
                ))}
              </div>
            )}
          </div>
        </BlurFade>

        {/* History */}
        {historyLocks.length > 0 && (
          <BlurFade delay={0.35}>
            <div className="mt-12 pt-8" style={{ borderTop: '1px solid hsl(35 12% 14%)' }}>
              <div className="flex items-center gap-2 mb-4">
                <History size={16} style={{ color: 'hsl(35 8% 52%)' }} />
                <h2
                  className="text-xl font-semibold"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: 'hsl(40 15% 75%)',
                  }}
                >
                  History
                </h2>
              </div>
              <div className="space-y-2">
                {historyLocks.map((lock) => (
                  <LockCard key={lock.id} lockId={lock.id} lock={lock} compact />
                ))}
              </div>
            </div>
          </BlurFade>
        )}
      </div>

      <CreateLockModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </PageLayout>
  );
};

export default DashboardPage;
